// src/components/CalculatorChart.js

import React, { useState, useMemo, useEffect } from 'react';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, ReferenceLine, Scatter } from 'recharts';
import { Typography, Switch, Space } from 'antd';
import styled from 'styled-components';

const { Title } = Typography;

const LegendItem = styled.span`
  cursor: pointer;
  opacity: ${props => props.active ? 1 : 0.5};
  transition: opacity 0.3s ease;
  color: black;
  font-size: 14px;
  font-weight: ${props => props.active ? 'normal' : 'normal'};

  &:hover {
    opacity: 0.8;
  }
`;

const LegendContainer = styled.div`
  padding-bottom: 20px;
`;

const colors = {
  netSalary: '#003f5c',
  netSalaryWithPension: '#58508d',
  totalTax: '#bc5090',
  totalNIC: '#ff6361',
  totalPension: '#ffa600',
  taxableIncome: '#8ac926',
  personalAllowance: '#4cc9f0',
  rsuTax: '#e76f51',
  nonRsuTax: '#2a9d8f',
  studentLoanRepayments: '#d62976' 
};

const CalculatorChart = ({ data, selectedTaxYear, hasStudentLoans = false }) => {
  const [includeRSU, setIncludeRSU] = useState(true);
  const [isAnnual, setIsAnnual] = useState(false);
  const [showTaxBreakdown, setShowTaxBreakdown] = useState(false);
  const [visibleSeries, setVisibleSeries] = useState({
    netSalary: true,
    netSalaryWithPension: true,
    totalTax: !showTaxBreakdown, // Hide when showing breakdown
    totalNIC: true,
    totalPension: true,
    taxableIncome: true,
    personalAllowance: true,
    rsuTax: showTaxBreakdown,
    nonRsuTax: showTaxBreakdown,
    studentLoanRepayments: hasStudentLoans
  });
  
  // State to track custom pension entry
  const [customPoint, setCustomPoint] = useState(null);

  useEffect(() => {
    setVisibleSeries(prev => ({
      ...prev,
      totalTax: !showTaxBreakdown,
      rsuTax: showTaxBreakdown,
      nonRsuTax: showTaxBreakdown
    }));
  }, [showTaxBreakdown]);

  useEffect(() => {
    setVisibleSeries(prev => ({
      ...prev,
      studentLoanRepayments: hasStudentLoans
    }));
  }, [hasStudentLoans]);

  // Sort the data by pension contribution percentage
  const sortedData = useMemo(() => {
    return [...data].sort((a, b) => 
      parseFloat(a.employee_pension_contribution_pct) - parseFloat(b.employee_pension_contribution_pct)
    );
  }, [data]);

  // Find custom pension entry
  useEffect(() => {
    const customEntry = data.find(item => String(item.id).startsWith('custom-'));
    if (customEntry) {
      setCustomPoint({
        x: parseFloat(customEntry.employee_pension_contribution_pct),
        y: parseFloat(includeRSU ? customEntry.net_salary : customEntry.net_salary_excl_rsu)
      });
    } else {
      setCustomPoint(null);
    }
  }, [data, includeRSU]);

  const chartData = useMemo(() => {
    return sortedData.map(item => ({
      employeePensionContributionPct: parseFloat(item.employee_pension_contribution_pct),
      isCustom: String(item.id).startsWith('custom-'),
      netSalary: parseFloat(includeRSU ? item.net_salary : item.net_salary_excl_rsu),
      netSalaryWithPension: parseFloat(includeRSU ? item.net_salary_inc_pension : item.net_salary_inc_pension_ex_RSU),
      totalTax: parseFloat(includeRSU ? item.total_tax : item.total_tax_excl_rsus),
      totalNIC: parseFloat(includeRSU ? item.total_nic : item.total_nic_excl_rsus),
      totalPension: parseFloat(item.total_pension_amount),
      taxableIncome: parseFloat(includeRSU ? item.taxable_income_less_pension : item.taxable_income_less_pension_excl_rsus),
      personalAllowance: Math.min(parseFloat(item.personal_allowance_reduction), 12570),

      // New properties for tax breakdown
      rsuTax: includeRSU ? parseFloat(item.rsu_tax || 0) : 0,
      nonRsuTax: includeRSU ? parseFloat(item.non_rsu_tax || 0) : parseFloat(item.total_tax_excl_rsus || 0),
      studentLoanRepayments: parseFloat(includeRSU ? item.student_loan_repayments : item.student_loan_repayments_excl_rsus)
    }));
  }, [sortedData, includeRSU]);

  // Extract custom data point for highlighting
  const customDataPoint = useMemo(() => {
    const customEntry = chartData.find(item => item.isCustom);
    return customEntry ? [customEntry] : [];
  }, [chartData]);

  // Determine pension % needed to maximize personal allowance achievable for current view
  const personalAllowanceInsight = useMemo(() => {
    if (!chartData || chartData.length === 0) return null;
    const maxAchievable = Math.max(...chartData.map(d => Number.isFinite(d.personalAllowance) ? d.personalAllowance : 0));
    if (!isFinite(maxAchievable)) return null;
    const target = chartData.find(d => (d.personalAllowance || 0) >= maxAchievable - 0.01);
    const targetPct = target ? target.employeePensionContributionPct : null;
    return { maxAchievable, targetPct };
  }, [chartData]);

  const getMaxValue = () => {
    return Math.max(...chartData.map(item => 
      Math.max(
        ...(Object.keys(visibleSeries)
          .filter(key => visibleSeries[key])
          .map(key => item[key]))
      )
    ));
  };

  const formatCurrency = (value) => {
    const amount = isAnnual ? value : value / 12;
    return new Intl.NumberFormat('en-GB', { style: 'currency', currency: 'GBP' }).format(amount);
  };

  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      // Check if this is the custom point
      const isCustomPoint = payload[0]?.payload?.isCustom;
      
      return (
        <div style={{ 
          backgroundColor: 'white', 
          padding: '10px', 
          border: isCustomPoint ? '2px solid red' : '1px solid #ccc',
          borderRadius: '5px', 
          boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
        }}>
          <p style={{ 
            textAlign: 'right', 
            margin: '0 0 5px', 
            fontWeight: 'bold',
            color: isCustomPoint ? 'red' : 'inherit'
          }}>
            {`Employee Pension Contribution: ${label}%`}
            {isCustomPoint && ' (Custom)'}
          </p>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <tbody>
              {payload.map((entry) => (
                (entry.dataKey !== 'isCustom' && visibleSeries[entry.dataKey]) && (
                  <tr key={entry.name} style={{ color: entry.color }}>
                    <td style={{ textAlign: 'left', padding: '2px 10px 2px 0' }}>{entry.name}:</td>
                    <td style={{ textAlign: 'right', padding: '2px 0' }}>{formatCurrency(entry.value)}</td>
                  </tr>
                )
              ))}
            </tbody>
          </table>
        </div>
      );
    }
    return null;
  };

  const handleLegendClick = (dataKey) => {
    setVisibleSeries(prevState => ({
      ...prevState,
      [dataKey]: !prevState[dataKey]
    }));
  };

  const CustomLegend = ({ payload }) => (
    <LegendContainer>
      <ul style={{ listStyle: 'none', padding: 0, display: 'flex', flexWrap: 'wrap', justifyContent: 'center' }}>
        {Object.entries(colors).map(([key, color]) => (
          (key !== 'studentLoanRepayments' || hasStudentLoans) && (
            <li key={key} style={{ margin: '0 10px' }}>
              <LegendItem
                active={visibleSeries[key]}
                color={color}
                onClick={() => handleLegendClick(key)}
              >
                <span style={{ display: 'inline-block', width: '10px', height: '10px', backgroundColor: color, marginRight: '5px' }}></span>
                {key.replace(/([A-Z])/g, ' $1').replace(/^./, str => str.toUpperCase())}
              </LegendItem>
            </li>
          )
        ))}
      </ul>
    </LegendContainer>
  );

  // Find the point where the pension contribution reaches or exceeds £60,000
  const targetPension = chartData.find(item => parseFloat(item.totalPension) >= 60000);

  // Create the reference line value
  const maxPensionContributionPct = targetPension ? targetPension.employeePensionContributionPct : null;

  return (
    <div style={{ width: '100%', marginBottom: 20 }}>
      <Title level={4}>
        {isAnnual ? 'Annual' : 'Monthly'} Amounts by Employee Pension Contribution %
      </Title>
      {/* Conversational explanation for axes, series, and toggles */}
      <div style={{ color: '#4b5563', fontSize: 13, marginTop: -4, marginBottom: 10, lineHeight: 1.5 }}>
        <div>
          Think of this chart like a what‑if dial. The X‑axis is your employee pension % and the Y‑axis shows money in £ ({isAnnual ? 'annual' : 'monthly'}).
        </div>
        <div>
          Each line tracks a different number as you move that % — Net Salary (with/without pension), Total Tax, National Insurance, Total Pension, Taxable Income, and your Personal Allowance.
          Use the legend to hide or show the lines you care about.
        </div>
        <div>
          The switches let you include RSUs, switch monthly/annual, and view tax as one line or split into RSU vs non‑RSU.
        </div>
      </div>

      {personalAllowanceInsight && (
        <div style={{ marginBottom: 12, fontSize: 13, fontWeight: 600, color: '#111827' }}>
          {personalAllowanceInsight.maxAchievable < 12570
            ? (
              <>With your current numbers, the most personal allowance you can unlock is £{personalAllowanceInsight.maxAchievable.toLocaleString(undefined, { maximumFractionDigits: 0 })}. You’ll get close at about {personalAllowanceInsight.targetPct?.toFixed(1)}% employee pension. Because taxable income over £100,000 tapers the allowance, £12,570 isn’t fully available here.</>
            ) : (
              <>To unlock the full personal allowance (£12,570), you’d need around {personalAllowanceInsight.targetPct?.toFixed(1)}% employee pension contribution.</>
            )}
        </div>
      )}
      <Space style={{ marginBottom: 12 }}>
        <span>Include RSU:</span>
        <Switch checked={includeRSU} onChange={setIncludeRSU} />
        <span>Show Annual Values:</span>
        <Switch checked={isAnnual} onChange={setIsAnnual} />
        <span>Show Tax Breakdown:</span>
        <Switch checked={showTaxBreakdown} onChange={setShowTaxBreakdown} />
      </Space>
      {/* Dedicated chart area so axis labels don't get clipped */}
      <div style={{ width: '100%', height: 520 }}>
        <ResponsiveContainer width="100%" height="100%">
          <LineChart
            data={chartData}
            margin={{ top: 10, right: 30, left: 20, bottom: 80 }}
          >
          <CartesianGrid strokeDasharray="3 3" stroke="#ccc" />
          <XAxis 
            dataKey="employeePensionContributionPct" 
            label={{ value: 'Employee Pension Contribution %', position: 'bottom', offset: -10 }}
            tick={{ fontSize: 12, fill: '#333' }}
            angle={-45}
            textAnchor="end"
            height={70}
          />
          <YAxis 
            label={{ 
              value: `Amount (£) - ${isAnnual ? 'Annual' : 'Monthly'}`, 
              angle: -90, 
              position: 'insideLeft', 
              offset: -10,
              style: { textAnchor: 'middle' }
            }}
            domain={[0, getMaxValue()]}
            tick={{ fontSize: 12, fill: '#333' }}
            tickFormatter={(value) => formatCurrency(value).replace('£', '')}
            width={80}
          />
          <Tooltip content={<CustomTooltip />} />
          <Legend content={<CustomLegend />} verticalAlign="top" height={50} />
          
          {/* Regular Lines */}
          {Object.entries(colors).map(([key, color]) => (
            (key !== 'studentLoanRepayments' || hasStudentLoans) && 
            visibleSeries[key] && (
              <Line 
                key={key}
                type="monotone" 
                dataKey={key} 
                name={key.replace(/([A-Z])/g, ' $1').replace(/^./, str => str.toUpperCase())} 
                stroke={color} 
                strokeWidth={2} 
                dot={false}
                connectNulls={true}
              />
            )
          ))}
          
          {/* Highlight for custom pension percentage */}
          {customDataPoint.length > 0 && Object.entries(colors).map(([key, color]) => (
            visibleSeries[key] && (
              <Scatter
                key={`scatter-${key}`}
                name=""
                data={customDataPoint}
                dataKey={key}
                fill="red"
                shape="circle"
                line={false}
                legendType="none"
              />
            )
          ))}
          
          {maxPensionContributionPct !== null && (
            <ReferenceLine 
              x={maxPensionContributionPct} 
              stroke="red" 
              strokeDasharray="3 3"
              label={{ 
                value: `Max Pension at ${maxPensionContributionPct.toFixed(1)}% (£60,000)`, 
                angle: 90, 
                position: 'insideRight', 
                fill: 'red', 
                offset: -10, 
                style: { textAnchor: 'middle' }
              }}
            />
          )}
          </LineChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
};

export default CalculatorChart;